import { InventoryCategoryTree } from '@atom/types/inventory';

type CategoryPathAcc = {
  categories: InventoryCategoryTree[];
  names: string[];
};

// returns the category path as names when given
// category path of ids
export const getCategoryPathNames = (
  tree: InventoryCategoryTree,
  categoryPath: string[],
): string[] => {
  if (!tree || !categoryPath || !categoryPath.length) {
    return [];
  }

  const { names } = categoryPath.reduce(
    (acc: CategoryPathAcc, id) => {
      const match = acc.categories.find(category => category.id === id);

      return match
        ? {
            categories: match.categories,
            names: [...acc.names, match.name],
          }
        : acc;
    },
    { categories: [tree], names: [] },
  );

  return names;
};

// returns the chain of category ids from the root to the target (inclusive),
// or null if the target is not found in the tree
export const getCategoryIdPath = (
  tree: InventoryCategoryTree,
  targetId: string,
  path: string[] = [],
): string[] | null => {
  if (!tree || !targetId) {
    return null;
  }

  if (tree.id === targetId) {
    return [...path, tree.id];
  }

  // use for loop for early return as soon as found
  for (const child of tree.categories ?? []) {
    const found = getCategoryIdPath(child, targetId, [...path, tree.id]);

    if (found) {
      return found;
    }
  }

  return null;
};

// recursively searches the category tree for category with the given id
export const getCategoryById = (
  id: string,
  trees: InventoryCategoryTree[],
): InventoryCategoryTree => {
  if (!id || !trees || !trees.length) {
    return null;
  }

  for (let index = 0; index < trees.length; index++) {
    const tree = trees[index];

    if (tree.id === id) {
      return tree;
    }

    const category = getCategoryById(id, tree?.categories || []);

    if (category) {
      return category;
    }
  }

  return null;
};

export const getCategoryDescendantSchemaIds = (
  trees: InventoryCategoryTree[],
): string[] => {
  if (!trees || !trees.length) {
    return [];
  }

  return trees.reduce((acc, tree) => {
    return [
      ...acc,
      ...(tree.schemaId ? [tree.schemaId] : []),
      ...getCategoryDescendantSchemaIds(tree.categories || []),
    ];
  }, []);
};

// recursively compiles all categories matching the given ids
export const getCategoriesByIds = (
  ids: string[],
  trees: InventoryCategoryTree[],
): InventoryCategoryTree[] => {
  if (!ids || !ids.length || !trees || !trees.length) {
    return [];
  }

  return trees.reduce((acc, tree) => {
    return [
      ...acc,
      ...(ids.includes(tree.id) ? [tree] : []),
      ...getCategoriesByIds(ids, tree.categories),
    ];
  }, []);
};

// getAllDescendantCategoryIds returns all descendant category ids for the given
// category tree node
export const getAllDescendantCategoryIds = (tree: InventoryCategoryTree) =>
  !tree || !tree.categories
    ? []
    : tree.categories.reduce(
        (acc: string[], category): string[] => [
          ...acc,
          category.id,
          ...getAllDescendantCategoryIds(category),
        ],
        [],
      );
